import {
  useNavigate,
  useLocation,
  Outlet,
  useParams,
  Navigate,
} from "react-router-dom";
import { useAppSelector, useAppDispatch } from "hooks";
import { useTranslation } from "react-i18next";
import { TopBar, Loading } from "@shopify/polaris";
import { RootState } from "configureStore"; // import RootState
import "./style.scss";
import { useMainMenuItems } from "pages/app/dashboard/Menu";
import { DashboardLayout } from "components/layout/DashboardLayout";
import { pathAfterCompanySwitch } from "pages/app/dashboard/company-switch-path";

import {
  logout,
  toogleUserMenu,
  toogleNav,
  fetchRole,
} from "features/auth/authSlice";
import { ExitIcon, ProfileIcon } from "@shopify/polaris-icons";
import Modalx from "components/modalx/modalx";
import OnlineUsersPanel from "features/presence/online-users-panel";
import { OnlineRailToggle } from "features/presence/online-rail-toggle";
import { MiniChatContainer } from "features/chat/mini-chat-container";
import { CallsProvider } from "features/calls/calls-provider";
import { AssistantContainer } from "features/assistant/assistant-container";
import { useEffect } from "react";
import { Role } from "types/Role";
import {
  useCabinetSummary,
  useCabinetSummaryRefresh,
} from "features/cabinet-tasks/use-cabinet-summary";
import {
  isVisitorPage,
  useCompanyAccessMode,
  useRecheckForbiddenCompany,
} from "features/company-select/group-visitor";
import { pickerUrlWithNotice } from "features/auth/access-lost";

type options = {
  value: string;
  label: string;
};

function Dashboard() {
  const { companyId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    session,
    userMenuActive,
    role,
  }: {
    session: { email: string; id: string } | null;
    isLoading: boolean;
    userMenuActive: boolean;
    isError: boolean;
    role: Role | null;
  } = useAppSelector((state: RootState) => state.authReducer);
  const dispatch = useAppDispatch();

  // A group visitor is not a member here: every request about this company
  // would be refused (the role request even logs the person out), so nothing
  // is asked until the person is known to be a member.
  const accessMode = useCompanyAccessMode(companyId);
  const isGroupVisitor = accessMode === "visitor";
  const canUseCompanyApi = accessMode === "regular";
  const isRecheckingAccess = useRecheckForbiddenCompany(companyId, accessMode);

  useEffect(() => {
    if (companyId && canUseCompanyApi) dispatch(fetchRole(companyId));
  }, [dispatch, companyId, canUseCompanyApi]);

  const { companiesCollection } = useAppSelector(
    (state: RootState) => state.companySelectReducer
  );

  const selectedCompany: options[] = companiesCollection.filter(
    (company: options) => company.value === companyId
  );

  // One request answers the bell, the «Užduotys» menu badge and the menu hints.
  // A group visitor is not a member, so it never asks.
  useCabinetSummaryRefresh(companyId, canUseCompanyApi);
  const { summary } = useCabinetSummary(companyId);
  // «Užduotys» in the menu shows the same number as the bell.
  const count = summary?.count ?? 0;
  const { MainMenuItems } = useMainMenuItems(companyId, { tasksCount: count });

  if (accessMode === "pending" || isRecheckingAccess) {
    return <Loading />;
  }

  // Not a member and not a group visitor, also after loading the lists again
  // (for example a dismissed employee).
  if (accessMode === "forbidden") {
    return <Navigate to={pickerUrlWithNotice("none")} replace />;
  }

  if (
    isGroupVisitor &&
    companyId &&
    !isVisitorPage(location.pathname, companyId)
  ) {
    return <Navigate to={`/app/${companyId}/posts`} replace />;
  }

  // A visitor never gets a role of this company, so it must not wait for one.
  if (!role && !isGroupVisitor) {
    return <Loading />;
  }

  const handleCompanyChange = (id: string) => {
    const newPath = pathAfterCompanySwitch(
      location.pathname,
      id,
      // Only member pages (which have a role) can be on a document opened from the profile.
      role?.user
    );
    if (newPath) navigate(newPath, { replace: true });
  };

  const handleLogOut = async () => {
    await dispatch(logout());
  };

  const handleToogleUserMenu = async () => {
    await dispatch(toogleUserMenu());
  };

  const handleNavigationToggle = async () => {
    await dispatch(toogleNav());
  };

  const handleSignatureList = () => {
    navigate(`/app/${companyId}/my-work`, { replace: true });
  };

  // A group visitor has no cabinet here, so it opens the one in their own company.
  // A guest member has only «About me» in the cabinet.
  const handleOpenCabinet = () => {
    const ownCompanyId = (companiesCollection as options[])[0]?.value;
    const cabinetCompanyId = isGroupVisitor ? ownCompanyId : companyId;
    if (!cabinetCompanyId) return;
    const cabinetPath = role?.isUserGuest
      ? `/app/${cabinetCompanyId}/profile/${session!.id}/info`
      : `/app/${cabinetCompanyId}/my-work`;
    navigate(cabinetPath, { replace: true });
  };

  const handleVisitGroupCompany = (id: string) => {
    navigate(`/app/${id}/posts`);
  };

  const userMenuActions = [
    {
      items: [
        {
          content: t("cabinet_group"),
          icon: ProfileIcon as any,
          onAction: handleOpenCabinet,
        },
        {
          content: t("log_out"),
          icon: ExitIcon as any,
          onAction: handleLogOut,
        },
      ],
    },
  ];
  const userMenuMarkup = (
    <TopBar.UserMenu
      initials=""
      actions={userMenuActions}
      name={session!.email}
      open={userMenuActive}
      onToggle={handleToogleUserMenu}
    />
  );

  return (
    <DashboardLayout
      menuItems={MainMenuItems}
      // The stored role may still be the previous company's; a visitor has none here
      role={isGroupVisitor ? null : role}
      companyId={companyId}
      onCompanyChange={handleCompanyChange}
      onVisitGroupCompany={handleVisitGroupCompany}
      isGroupVisitor={isGroupVisitor}
      onlineUsersButton={<OnlineRailToggle />}
      onNavigationToggle={handleNavigationToggle}
      userMenuMarkup={userMenuMarkup}
      notificationCount={count}
      onNotificationClick={handleSignatureList}
      selectedCompany={
        selectedCompany.length > 0 ? selectedCompany[0] : undefined
      }
    >
      <Outlet />
      <Modalx />
      {!isGroupVisitor && (
        <>
          {/* wraps the chat surfaces so a call can be started from any of them and
              the incoming-call screen has one home */}
          <CallsProvider>
            <OnlineUsersPanel />
            <MiniChatContainer />
          </CallsProvider>
          <AssistantContainer />
        </>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;
